import React, { useEffect, useState } from "react";
import {
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
  MinusCircleOutlined,
} from "@ant-design/icons";
import {
  Form,
  Input,
  Button,
  message,
  Switch,
  InputNumber,
  Image,
  Tag,
  Popconfirm,
  Spin,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import ButtonWrapper from "../../../components/reuseComp/ButtonWrapper";
import UploadFoodImage from "../../../../lib/UploadFoodImage";
import {
  useServiceList,
  useServiceDelete,
  useServiceSave,
  useServiceDetail,
} from "../../../../hooks/api/service/hooks";
import { IServicePayload, ServiceItem } from "../../../../types/api/service";
import { removeSpecialChars } from "../../../../lib/utils";
import CommonListCard from "../../../../views/components/reuseComp/CommonListCard";

const ServiceListingSection = ({ allowURLStateUpdate = true }) => {
  const [messageApi, contextHolder] = message.useMessage();

  const DEFAULT_NUQS_CONFIG = {
    throttleMs: !allowURLStateUpdate ? Infinity : 0,
  };

  const {
    data,
    refetchWithQuery: refetch,
    isFetching,
  } = useServiceList({
    page: 1,
    limit: 20,
    ...DEFAULT_NUQS_CONFIG,
  });
  const serviceList: ServiceItem[] = data?.data ?? [];
  const qData = data?.qData ?? { page: 1, limit: 20 };

  const { mutate: saveService, isPending } = useServiceSave({ qData });
  const { mutate: deleteService } = useServiceDelete({ qData });

  const [selectedSlug, setSelectedSlug] = useState<string>("");
  const { data: serviceDetail, isFetching: isDetailFetching } =
    useServiceDetail(selectedSlug);

  const [editingService, setEditingService] = useState<IServicePayload | null>(
    null,
  );
  const [Editing, setEditing] = useState(false);

  const [form] = Form.useForm();
  const watchHeading = Form.useWatch(["banner", "heading"], form);

  const handleDelete = (id: string) => {
    deleteService(
      { id },
      {
        onSuccess: () => {
          messageApi.success("Service deleted successfully");
          refetch({ page: qData.page, limit: qData.limit });
        },
        onError: (error) => {
          messageApi.error("Failed to delete service");
          console.error(error);
        },
      },
    );
  };

  const handleCancel = () => {
    setEditingService(null);
    setEditing(false);
    setSelectedSlug("");
    form.resetFields();
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      const payload: IServicePayload = {
        _id: editingService?._id,
        slug: values.slug,
        banner: { heading: values.banner.heading },
        metaDescription: values.metaDescription,
        overview: {
          content: values.overview?.content ?? [],
          image: values.overview?.image,
        },
        order: values.order ?? 0,
        status: values.status ?? false,
        isDeleted: false,
      };

      saveService(payload, {
        onSuccess: () => {
          messageApi.success("Service saved successfully");
          refetch({ page: qData.page, limit: qData.limit, _ts: Date.now() });
          handleCancel();
        },
        onError: (error) => {
          messageApi.error("Failed to save service");
          console.error(error);
        },
      });
    } catch (err) {
      console.log("Validation failed:", err);
    }
  };

  useEffect(() => {
    if (serviceDetail) {
      setEditingService(serviceDetail?.data);
    }
  }, [serviceDetail]);

  useEffect(() => {
    if (editingService) {
      form.setFieldsValue({
        banner: { heading: editingService?.banner?.heading },
        slug: editingService.slug,
        metaDescription: editingService.metaDescription,
        overview: {
          content: editingService?.overview?.content ?? [],
          image: editingService?.overview?.image ?? "",
        },
        order: editingService.order,
        status: editingService.status,
      });
    }
  }, [editingService, form]);

  useEffect(() => {
    if (!editingService?._id) {
      form.setFieldValue("slug", removeSpecialChars(watchHeading));
    }
  }, [watchHeading, editingService, form]);

  const handleEditClick = (item: ServiceItem) => {
    setSelectedSlug(item.slug);
    setEditingService(null);
    setEditing(true);
  };

  return (
    <div>
      {contextHolder}

      {Editing ? (
        <div>
          {isDetailFetching && <Spin tip="Loading service details..." />}
          <div className="bg-[#F9F9F9] p-4 sm:p-6 rounded-2xl shadow-sm mb-6">
            <Form
              layout="vertical"
              form={form}
              className="grid grid-cols-1 sm:grid-cols-2 gap-4"
            >
              <Form.Item
                label="Heading"
                name={["banner", "heading"]}
                rules={[{ required: true, message: "Heading is required" }]}
              >
                <Input placeholder="enter heading..." />
              </Form.Item>

              
              <Form.Item
                label="Meta Description"
                name="metaDescription"
                rules={[{ required: true, message: "Meta Description is required" }]}
              >
                <TextArea rows={2} placeholder="enter meta description..."/>
              </Form.Item>

              <Form.Item
                label="Slug"
                name="slug"
                rules={[{ required: true, message: "Slug is required" }]}
              >
                <Input disabled={!!editingService?._id} />
              </Form.Item>

              <Form.Item label="Status" name="status" valuePropName="checked">
                <Switch checkedChildren="Active" unCheckedChildren="Inactive" />
              </Form.Item>

              <Form.Item label="Order" name="order">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>

              <Form.Item
                label="Image"
                name={["overview", "image"]}
                rules={[{ required: true, message: "Image is required" }]}
                className="sm:col-span-2"
              >
                <UploadFoodImage maxUploadCount={1} />
              </Form.Item>

              <Form.Item label="Content" className="sm:col-span-2">
                <Form.List name={["overview", "content"]}>
                  {(fields, { add, remove }) => (
                    <>
                      {fields.map(({ key, name }) => (
                        <div key={key} className="flex gap-2 items-center mb-2">
                          <Form.Item
                            name={name}
                            rules={[
                              { required: true, message: "Enter content" },
                            ]}
                            className="flex-1 mb-0"
                          >
                            <TextArea rows={3} />
                          </Form.Item>
                          <MinusCircleOutlined
                            className="text-red-500 text-lg cursor-pointer"
                            onClick={() => remove(name)}
                          />
                        </div>
                      ))}

                      <Button
                        type="dashed"
                        onClick={() => add()}
                        icon={<PlusOutlined />}
                        block
                      >
                        Add Content
                      </Button>
                    </>
                  )}
                </Form.List>
              </Form.Item>
            </Form>
          </div>

          <div className="flex justify-start gap-3 mt-7 sticky bottom-5">
            <ButtonWrapper
              type="primary"
              onClick={handleSave}
              loading={isPending}
            >
              Save
            </ButtonWrapper>
            <ButtonWrapper onClick={handleCancel}>Cancel</ButtonWrapper>
          </div>
        </div>
      ) : (
        <div className="rounded-[20px] overflow-hidden">
          <div className="hidden sm:block overflow-hidden rounded-[20px]">
            <table className="hidden sm:table min-w-full">
              <thead>
                <tr className="bg-primary text-white">
                  <th className="py-3 px-4 text-left text-sm md:text-base">
                    Image
                  </th>
                  <th className="py-3 px-4 text-left text-sm md:text-base">
                    Heading
                  </th>
                  <th className="py-3 px-4 text-left text-sm md:text-base">
                    Subtext
                  </th>
                  <th className="py-3 px-4 text-left text-sm md:text-base">
                    Order
                  </th>
                  <th className="py-3 px-4 text-left text-sm md:text-base">
                    Status
                  </th>
                  <th className="py-3 px-4 text-right text-sm md:text-base">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {serviceList?.map((item) => (
                  <tr
                    className="bg-[#F9F9F9] border-b-2 border-[#e9e8e8]"
                    key={item._id}
                  >
                    <td className="py-3 px-4 ">
                      <Image
                        src={item?.image}
                        alt={item?.heading || "Service"}
                        width={60}
                        height={60}
                        fallback="/placeholder.jpg"
                        className="rounded-md object-cover"
                      />
                    </td>
                    <td className="py-3 px-4 ">{item?.heading}</td>
                    <td className="py-3 px-4 ">
                      <p className="line-clamp-2">{item?.content?.[0]}</p>
                    </td>
                    <td className="py-3 px-4 ">{item?.order}</td>
                    <td className="py-3 px-4 ">
                      <Tag color={item?.status ? "green" : "red"}>
                        {item?.status ? "Active" : "Inactive"}
                      </Tag>
                    </td>
                    <td className="py-3 px-4 text-primary align-middle">
                      <div className="flex justify-center items-center gap-4 text-lg">
                        <EditOutlined
                          onClick={() => handleEditClick(item)}
                          className="cursor-pointer text-primary"
                        />
                        <Popconfirm
                          title="Are you sure to delete this service?"
                          onConfirm={() => handleDelete(item?._id || "")}
                        >
                          <DeleteOutlined className="cursor-pointer text-red-500" />
                        </Popconfirm>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="bg-[#F9F9F9] shadow-sm p-2 sm:p-3">
              <div className="flex justify-end items-center">
                <Button
                  icon={<PlusOutlined />}
                  onClick={() => setEditing(true)}
                  type="dashed"
                  className="text-primary font-semibold"
                >
                  Add Service
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      <div className="sm:hidden space-y-4">
        {!Editing && (
          <>
            {serviceList?.map((service) => (
              <CommonListCard
                key={service._id}
                service={service}
                form={form}
                setEditingService={setEditingService}
                setEditing={setEditing}
                handleDelete={handleDelete}
                handleEditClick={handleEditClick}
              />
            ))}

            <div className="flex justify-end items-center">
              <Button
                icon={<PlusOutlined />}
                onClick={() => setEditing(true)}
                type="dashed"
                className="text-primary font-semibold"
              >
                Add Service
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ServiceListingSection;
